import type { StripeAccount, StripePerson } from '@klear/stripe'
import { Timestamp } from '@klear/time'
import * as changeCase from 'change-case'
import { COUNTRY_CODE_US } from '@klear/constants/countries'
import { kycPersonViewsFromStripe, type KycPersonView } from '@klear/kyc/person'

export type KycModelJson = {
  organizationId: number
  stripeAccount?: StripeAccount | null
  stripePersons?: StripePerson[] | null
}

export class KycModel {
  static readonly __klearClass = 'KycModel'

  organizationId: number
  #stripeAccount: StripeAccount
  stripePersons: StripePerson[]
  #ignoredRequirements = ['external_account']
  #getFilteredRequirements = (requirements: string[] | null | undefined) => {
    return (requirements || []).filter((requirement: string) => !this.#ignoredRequirements.includes(requirement))
  }

  constructor(props: KycModelJson = { organizationId: 0 }) {
    this.organizationId = props.organizationId
    this.#stripeAccount = props.stripeAccount ?? ({} as StripeAccount)
    this.stripePersons = props.stripePersons ?? []
  }

  get stripeAccount(): StripeAccount {
    return this.#stripeAccount
  }

  toJSON(): KycModelJson {
    return {
      organizationId: this.organizationId,
      stripeAccount: this.stripeAccount,
      stripePersons: this.stripePersons
    }
  }

  get id() {
    return this.#stripeAccount.id
  }

  get persons(): KycPersonView[] {
    return kycPersonViewsFromStripe(this.stripePersons)
  }

  get currentRequirements() {
    return this.#getFilteredRequirements(this.#stripeAccount.requirements?.currently_due)
  }

  get pastDueRequirements() {
    return this.#getFilteredRequirements(this.#stripeAccount.requirements?.past_due)
  }

  get futureRequirements() {
    return this.#getFilteredRequirements(this.#stripeAccount.requirements?.eventually_due)
  }

  get requirementsTypes(): string[] {
    const requirements = [...this.currentRequirements, ...this.pastDueRequirements, ...this.futureRequirements]
    return [
      ...requirements.reduce((acc: Set<string>, requirement: string) => {
        const pieces = requirement.split('.')
        if (pieces.length > 1) {
          if (pieces[0]?.startsWith('person_')) {
            const person = this.stripePersons.find((person) => person.id === pieces[0])
            if (person) {
              acc.add(`${person.first_name} ${person.last_name} - ${pieces[1] === 'id_number' ? 'SSN' : pieces[1]}`)
            } else {
              console.warn('person not found', pieces[0], this.stripePersons)
              acc.add(`${pieces[0]} - ${pieces[1]}`)
            }
          } else {
            acc.add(pieces.map((piece) => changeCase.sentenceCase(piece)).join(' -> '))
          }
        }
        return acc
      }, new Set<string>())
    ]
  }

  get errors() {
    return this.#stripeAccount.requirements?.errors ?? []
  }

  get termsOfServiceSigned() {
    return this.#stripeAccount.tos_acceptance?.date
  }

  get pendingVerification() {
    return this.#stripeAccount.requirements?.pending_verification ?? []
  }

  get currentDeadline(): Timestamp | undefined {
    if (this.#stripeAccount.requirements?.current_deadline) {
      return Timestamp.fromEpochSeconds(this.#stripeAccount.requirements.current_deadline)
    }
    return undefined
  }

  get isVerified() {
    return this.status === 'verified'
  }

  get status() {
    if (!this.termsOfServiceSigned) {
      return 'incomplete'
    }

    if (!this.pastDueRequirements.length) {
      return 'verified'
    }

    if (this.errors.length) {
      return 'failed'
    }

    if (this.pendingVerification.length) {
      return 'pending'
    }

    return 'incomplete'
  }

  get country() {
    return this.#stripeAccount.country
  }

  get canCreateKlearAccount() {
    return this.isVerified && this.country === COUNTRY_CODE_US
  }

  get klearAccountNotAvailableInCountry() {
    return this.isVerified && !!this.country && this.country !== COUNTRY_CODE_US
  }

  get businessName() {
    return this.#stripeAccount.business_profile?.name || ''
  }

  get businessType() {
    return this.#stripeAccount.business_type
  }

  get email() {
    return this.#stripeAccount.email
  }

  get companyName() {
    return this.#stripeAccount.company?.name ?? ''
  }

  get companyPhone() {
    return this.#stripeAccount.company?.phone ?? ''
  }

  get companyStructure() {
    return this.#stripeAccount.company?.structure ?? ''
  }

  get companyAddressLine1() {
    return this.#stripeAccount.company?.address?.line1 ?? ''
  }

  get businessProfileUrl() {
    return this.#stripeAccount.business_profile?.url ?? ''
  }

  get businessProfileSupportPhone() {
    return this.#stripeAccount.business_profile?.support_phone ?? ''
  }

  get businessProfileMcc() {
    return this.#stripeAccount.business_profile?.mcc ?? ''
  }

  get statementDescriptor() {
    return this.#stripeAccount.settings?.payments?.statement_descriptor ?? ''
  }

  get capabilities() {
    return this.#stripeAccount.capabilities ?? {}
  }

  get hasPersonOutstandingRequirements() {
    return this.persons.some((person) => person.hasOutstandingRequirements)
  }
}
